import { useState } from 'react';
import { updateCourse } from '../../services/content/courseService';
import type { Course, CourseVideo } from '../../types/content/course';

type View = 'list' | 'detail';

interface CourseVideosModalProps {
  course: Course;
  onClose: () => void;
  onSuccess: () => void;
}

export function CourseVideosModal({ course, onClose, onSuccess }: CourseVideosModalProps) {
  const [videos, setVideos] = useState<CourseVideo[]>(
    course.videos ? JSON.parse(JSON.stringify(course.videos)) : []
  );
  const [view, setView] = useState<View>('list');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [videoTitle, setVideoTitle] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const btnSmall = 'text-xs px-2 py-1 rounded transition-colors';

  function openVideoForm(index: number | null) {
    if (index !== null) {
      const v = videos[index];
      setVideoTitle(v.title);
      setVideoUrl(v.url);
    } else {
      setVideoTitle('');
      setVideoUrl('');
    }
    setEditingIndex(index);
    setView('detail');
  }

  function saveVideoItem() {
    if (!videoTitle.trim() || !videoUrl.trim()) return;
    const updated = [...videos];
    const item: CourseVideo = {
      order: editingIndex !== null ? videos[editingIndex].order : videos.length + 1,
      title: videoTitle.trim(),
      url: videoUrl.trim(),
    };
    if (editingIndex !== null) {
      updated[editingIndex] = item;
    } else {
      updated.push(item);
    }
    setVideos(updated);
    setView('list');
  }

  function deleteVideo(index: number) {
    const updated = videos.filter((_, i) => i !== index);
    // Re-order
    setVideos(updated.map((v, i) => ({ ...v, order: i + 1 })));
  }

  function moveVideo(index: number, direction: -1 | 1) {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= videos.length) return;
    const updated = [...videos];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setVideos(updated.map((v, i) => ({ ...v, order: i + 1 })));
  }

  async function handleSave() {
    setError('');
    setLoading(true);
    try {
      await updateCourse(course.id, { ...course, videos });
      onSuccess();
    } catch {
      setError('Failed to update videos');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">

        {view === 'list' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-1">
                Videos — {course.name}
              </h2>
              <p className="text-sm text-gray-500 mb-4">{videos.length} video(s)</p>

              <div className="flex justify-end mb-2">
                <button type="button" onClick={() => openVideoForm(null)} className={`${btnSmall} text-blue-600 hover:bg-blue-50`}>+ Add Video</button>
              </div>

              {videos.length === 0 ? (
                <p className="text-sm text-gray-400">No videos yet</p>
              ) : (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {videos.map((video, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs text-gray-400 w-5 text-center">{video.order}</span>
                        <div className="min-w-0">
                          <div className="text-sm text-gray-700 truncate">{video.title}</div>
                          <div className="text-xs text-gray-400 truncate">{video.url}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        <button type="button" onClick={() => moveVideo(i, -1)} disabled={i === 0} className={`${btnSmall} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}>&#9650;</button>
                        <button type="button" onClick={() => moveVideo(i, 1)} disabled={i === videos.length - 1} className={`${btnSmall} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}>&#9660;</button>
                        <button type="button" onClick={() => openVideoForm(i)} className={`${btnSmall} text-gray-500 hover:bg-gray-100`}>Edit</button>
                        <button type="button" onClick={() => deleteVideo(i)} className={`${btnSmall} text-red-500 hover:bg-red-50`}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
              <button type="button" onClick={handleSave} disabled={loading} className={btnPrimary}>
                {loading ? 'Saving...' : 'Save Videos'}
              </button>
            </div>
          </>
        )}

        {view === 'detail' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {editingIndex !== null ? 'Edit Video' : 'Add Video'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Title *</label>
                  <input type="text" value={videoTitle} onChange={(e) => setVideoTitle(e.target.value)} className={inputClass} placeholder="Video title" />
                </div>
                <div>
                  <label className={labelClass}>URL *</label>
                  <input type="text" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} className={inputClass} placeholder="https://..." />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setView('list')} className={btnOutline}>Back</button>
              <button type="button" onClick={saveVideoItem} disabled={!videoTitle.trim() || !videoUrl.trim()} className={btnPrimary}>Done</button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
